import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "golf-map-container" }
const _hoisted_2 = { class: "map-wrapper" }
const _hoisted_3 = { class: "map-controls" }
const _hoisted_4 = { class: "filters-section" }
const _hoisted_5 = { class: "status-filters" }
const _hoisted_6 = { class: "stats-section" }
const _hoisted_7 = { class: "stat-item" }
const _hoisted_8 = { class: "stat-label" }
const _hoisted_9 = { class: "stat-value" }
const _hoisted_10 = { class: "stat-item" }
const _hoisted_11 = { class: "stat-label" }
const _hoisted_12 = { class: "stat-value" }
const _hoisted_13 = { class: "stat-item" }
const _hoisted_14 = { class: "stat-label" }
const _hoisted_15 = { class: "stat-value" }
const _hoisted_16 = { class: "actions-section" }
const _hoisted_17 = { class: "map-container" }
const _hoisted_18 = {
  key: 0,
  class: "map-loading"
}
const _hoisted_19 = {
  key: 0,
  class: "map-error"
}
const _hoisted_20 = {
  key: 1,
  class: "map-loading"
}
const _hoisted_21 = {
  key: 0,
  class: "car-details"
}
const _hoisted_22 = { class: "detail-item" }
const _hoisted_23 = { class: "detail-item" }
const _hoisted_24 = { class: "detail-item" }
const _hoisted_25 = { class: "detail-item" }
const _hoisted_26 = { class: "detail-item" }
const _hoisted_27 = { class: "detail-item" }
const _hoisted_28 = { class: "detail-item" }
const _hoisted_29 = { class: "battery-info" }
const _hoisted_30 = { class: "detail-item" }

import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import CommonError from '@/components/CommonError.vue'
import { routeNames } from '@/router/route-names'
import { useGolfVehiclesStore } from '@/store/golf-vehicles'
import type { GolfCarProfile } from '@/api/golf'
import { getGolfAreas, type GolfArea } from '@/api/golf-areas'
import { useGolfHeaderStore } from '@/store/golf-header'
import { useGolfI18n } from '@/composables/useGolfI18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'GolfMap',
  setup(__props) {

const router = useRouter()
const golfStore = useGolfVehiclesStore()
const headerStore = useGolfHeaderStore()
const { t } = useGolfI18n()

const selectedStatuses = ref<string[]>(['active', 'inactive', 'admin_blocked'])
const mapInitialized = ref(false)
const mapContainer = ref<HTMLElement | null>(null)
const leafletMap = ref<L.Map | null>(null)
const areasLayerGroup = ref<L.LayerGroup | null>(null)
const golfAreas = ref<GolfArea[]>([])
const showCarDialog = ref(false)
const selectedCar = ref<GolfCarProfile | null>(null)

const carStatuses = computed(() => [
  { value: 'active', label: t('golf.common.active'), color: '#67C23A' },
  { value: 'inactive', label: t('golf.fleet.status_inactive'), color: '#E6A23C' },
  { value: 'admin_blocked', label: t('golf.common.blocked'), color: '#F56C6C' },
])

const filteredGolfCars = computed(() => {
  return golfStore.profiles.filter((car: GolfCarProfile) =>
    selectedStatuses.value.includes(car.status)
  )
})

const totalCarsCount = computed(() => golfStore.profiles.length)

const getCountByStatus = (status: string) => {
  return golfStore.profiles.filter((car: GolfCarProfile) => car.status === status).length
}

const filterChanged = () => {
  updateMapMarkers()
}

const formatStatus = (status: string) => {
  const map: Record<string, string> = {
    active: t('golf.common.active'),
    inactive: t('golf.fleet.status_inactive'),
    admin_blocked: t('golf.common.blocked'),
  }
  return map[status] || status
}

const getStatusType = (status: string) => {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'warning'
    case 'admin_blocked':
      return 'danger'
    default:
      return 'info'
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return '#67C23A'
    case 'inactive':
      return '#E6A23C'
    case 'admin_blocked':
      return '#F56C6C'
    default:
      return '#909399'
  }
}

const getAvgBattery = (assets?: any[]) => {
  if (!assets || assets.length === 0) return 0
  const total = assets.reduce((sum, asset) => sum + (asset.battery_pct || 0), 0)
  return Math.round(total / assets.length)
}

const getBatteryColor = (percentage: number) => {
  if (percentage <= 25) return '#F56C6C'
  if (percentage <= 50) return '#E6A23C'
  return '#67C23A'
}

const loadGolfCars = () => {
  golfStore.fetchProfiles()
}

const refreshData = () => {
  golfStore.fetchProfiles()
}

const initializeMap = () => {
  if (!mapContainer.value) return

  if (leafletMap.value) {
    leafletMap.value.remove()
    leafletMap.value = null
  }

  delete (L.Icon.Default.prototype as any)._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  })

  const defaultLat = 40.3775
  const defaultLng = 49.8671

  const map = L.map(mapContainer.value).setView([defaultLat, defaultLng], 12)
  leafletMap.value = map

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap contributors',
  }).addTo(map)

  areasLayerGroup.value = L.layerGroup().addTo(map)

  updateMapMarkers()
  mapInitialized.value = true

  setTimeout(() => {
    map.invalidateSize()
  }, 100)
}

const updateMapMarkers = () => {
  if (!leafletMap.value) return

  leafletMap.value.eachLayer((layer: any) => {
    if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
      leafletMap.value?.removeLayer(layer)
    }
  })

  filteredGolfCars.value.forEach((car: GolfCarProfile) => {
    car.assets?.forEach((asset: any) => {
      if (asset.lat && asset.lng) {
        const color = getStatusColor(car.status)
        const marker = L.circleMarker([asset.lat, asset.lng], {
          radius: 10,
          fillColor: color,
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
        })

        marker.bindPopup(`
          <div class="marker-popup">
            <strong>${car.name}</strong><br/>
            Status: ${formatStatus(car.status)}<br/>
            Battery: ${asset.battery_pct}%
          </div>
        `)

        marker.on('click', () => {
          selectedCar.value = car
          showCarDialog.value = true
        })

        if (leafletMap.value) marker.addTo(leafletMap.value as L.Map)
      }
    })
  })
}

const goToDetails = (carId: string) => {
  router.push({ name: routeNames.golfCarDetails, params: { id: carId } })
}

const renderAreas = () => {
  if (!areasLayerGroup.value || !golfAreas.value.length) return

  areasLayerGroup.value.clearLayers()

  golfAreas.value.forEach((area) => {
    if (area.type !== 'parking') return
    if (!area.is_active) return
    if (!area.points || area.points.length < 3) return

    const latLngs = area.points.map((p) => [p.lat, p.lng] as [number, number])

    const polygon = L.polygon(latLngs, {
      color: '#2eb872',
      weight: 1.5,
      opacity: 0.35,
      fillColor: '#2eb872',
      fillOpacity: 0.08,
      interactive: false,
    })

    areasLayerGroup.value?.addLayer(polygon)
  })
}

const loadGolfAreas = async () => {
  try {
    const response = await getGolfAreas()
    if (response?.data?.data) {
      golfAreas.value = response.data.data.data || []
      renderAreas()
    }
  } catch (e) {
    console.error('Failed to load golf areas', e)
  }
}

watch(() => golfStore.profiles, () => {
  if (mapInitialized.value) {
    updateMapMarkers()
  }
}, { deep: true })

onMounted(async () => {
  headerStore.setHeaderInfo(t('golf.map.title'), false)
  await nextTick()
  initializeMap()
  golfStore.fetchProfiles()
  loadGolfAreas()
})

return (_ctx: any,_cache: any) => {
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_checkbox_group = _resolveComponent("el-checkbox-group")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_progress = _resolveComponent("el-progress")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(golfStore).error)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 0,
          errorMessage: _unref(golfStore).error,
          onRetryPress: loadGolfCars
        }, null, 8, ["errorMessage"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h4", null, _toDisplayString(_ctx.$t('golf.fleet.filter_by_status')), 1),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_el_checkbox_group, {
              modelValue: selectedStatuses.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedStatuses).value = $event)),
              onChange: filterChanged
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carStatuses.value, (status) => {
                  return (_openBlock(), _createBlock(_component_el_checkbox, {
                    key: status.value,
                    label: status.value,
                    class: "status-checkbox"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(status.label) + " (" + _toDisplayString(getCountByStatus(status.value)) + ") ", 1)
                    ]),
                    _: 2
                  }, 1032, ["label"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('golf.map.total_golf_cars')), 1),
            _createElementVNode("span", _hoisted_9, _toDisplayString(totalCarsCount.value), 1)
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('golf.map.visible')), 1),
            _createElementVNode("span", _hoisted_12, _toDisplayString(filteredGolfCars.value.length), 1)
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('golf.map.available')), 1),
            _createElementVNode("span", _hoisted_15, _toDisplayString(getCountByStatus('active')), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: refreshData,
            loading: _unref(golfStore).isLoading
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('golf.common.refresh')), 1)
            ]),
            _: 1
          }, 8, ["loading"])
        ])
      ]),
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("div", {
          ref_key: "mapContainer",
          ref: mapContainer,
          id: "golf-map-container",
          class: "leaflet-map"
        }, null, 512),
        (_unref(golfStore).isLoading && !_unref(golfStore).profiles.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('golf.map.loading_map')), 1),
              (_unref(golfStore).error)
                ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(_unref(golfStore).error), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (!leafletMap.value && !_unref(golfStore).isLoading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t('golf.map.initializing_map')), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_el_dialog, {
        modelValue: showCarDialog.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showCarDialog).value = $event)),
        title: 
          _ctx.$t('golf.map.golf_car_title', { name: selectedCar.value?.name || '' })
        ,
        width: "600px"
      }, {
        default: _withCtx(() => [
          (selectedCar.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createVNode(_component_el_row, { gutter: 20 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_22, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.common.name')) + ":", 1),
                          _createTextVNode(" " + _toDisplayString(selectedCar.value.name), 1)
                        ]),
                        _createElementVNode("div", _hoisted_23, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.fleet.model_name')) + ":", 1),
                          _createTextVNode(" " + _toDisplayString(selectedCar.value.model_name), 1)
                        ]),
                        _createElementVNode("div", _hoisted_24, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.trips.seats')) + ":", 1),
                          _createTextVNode(" " + _toDisplayString(selectedCar.value.seats), 1)
                        ]),
                        _createElementVNode("div", _hoisted_25, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.common.status')) + ":", 1),
                          _createVNode(_component_el_tag, {
                            type: getStatusType(selectedCar.value.status)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(formatStatus(selectedCar.value.status)), 1)
                            ]),
                            _: 1
                          }, 8, ["type"])
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, { span: 12 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_26, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.fleet.physical_assets')) + ":", 1),
                          _createTextVNode(" " + _toDisplayString(selectedCar.value.assets?.length || 0), 1)
                        ]),
                        _createElementVNode("div", _hoisted_27, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.map.available_assets')), 1),
                          _createTextVNode(" " + _toDisplayString(selectedCar.value.assets?.filter((a) => a.status === 'available').length || 0), 1)
                        ]),
                        _createElementVNode("div", _hoisted_28, [
                          _createElementVNode("strong", null, _toDisplayString(_ctx.$t('golf.fleet.battery')), 1),
                          _createElementVNode("div", _hoisted_29, [
                            _createVNode(_component_el_progress, {
                              percentage: getAvgBattery(selectedCar.value.assets),
                              color: getBatteryColor(getAvgBattery(selectedCar.value.assets))
                            }, null, 8, ["percentage", "color"])
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_30, [
                          _createVNode(_component_el_button, {
                            type: "primary",
                            size: "small",
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (goToDetails(selectedCar.value.id)))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('golf.trips.view_details')), 1)
                            ]),
                            _: 1
                          })
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "title"])
    ])
  ]))
}
}

})